<template>
  <div class="file-details-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="breadcrumb-nav">
        <router-link :to="{ name: 'chatbot' }" class="breadcrumb-link">
          <i class="las la-home"></i>
          Financial Assistant
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <router-link :to="`/manage/chatbot/candidate/${candidateId}/sessions`" class="breadcrumb-link">
          Chat Sessions
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <router-link :to="`/manage/chatbot/candidate/${candidateId}/files`" class="breadcrumb-link">
          Files
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">File Details</span>
      </div>
      <h1 class="page-title ml-3">{{ fileDetails && fileDetails.file_name ? fileDetails.file_name : 'File Details' }}</h1>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading file details...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="las la-exclamation-triangle"></i>
        </div>
        <h3>Error Loading File</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button @click="loadFileDetails" class="retry-btn">
            <i class="las la-redo"></i>
            Try Again
          </button>
          <router-link :to="`/manage/chatbot/candidate/${candidateId}/files`" class="back-btn">
            <i class="las la-arrow-left"></i>
            Back to Files
          </router-link>
        </div>
      </div>

      <div v-else-if="fileDetails" class="file-details-content">
        <!-- File Overview Card -->
        <div class="file-overview-card">
          <div class="file-header">
              <i class="las la-file-pdf"></i>
            <div class="file-info">
              <h2 class="file-name">{{ fileDetails.name }}</h2>
              <div class="file-meta">
                <span class="file-type">{{ getDocumentTypeLabel(fileDetails.type) }}</span>
                <span class="file-size" v-if="fileDetails.file_size">{{ formatFileSize(fileDetails.file_size) }}</span>
                <span class="file-date" v-if="fileDetails.uploaded_at">{{ formatDate(fileDetails.uploaded_at) }}</span>
              </div>
            </div>
            <div class="file-status">
              <span class="status-badge" :class="getStatusClass(fileDetails.status)">
                {{ getStatusLabel(fileDetails.status) }}
              </span>
            </div>
          </div>
        </div>


        <!-- Analysis Results -->
        <div class="details-card" v-if="fileDetails.analysis_results">
          <h3 class="card-title">
            <i class="las la-chart-bar"></i>
            Analysis Results
          </h3>
          <div class="analysis-content">
            <div v-if="fileDetails.analysis_results.summary" class="analysis-summary">
              <h4>Summary</h4>
              <p>{{ fileDetails.analysis_results.summary }}</p>
            </div>
            <div v-if="fileDetails.analysis_results.key_findings" class="key-findings">
              <h4>Key Findings</h4>
              <ul>
                <li v-for="finding in fileDetails.analysis_results.key_findings" :key="finding">
                  {{ finding }}
                </li>
              </ul>
            </div>
          </div>
          <!-- Error Information -->
          <div class="details-card" v-if="fileDetails.error_message">
            <h3 class="card-title error-title">
              <i class="las la-exclamation-triangle"></i>
              Error Information
            </h3>
            <div class="error-content">
              <p class="error-message">{{ fileDetails.error_message }}</p>
              <div v-if="fileDetails.error_code" class="error-code">
                <span class="detail-label">Error Code:</span>
                <span class="detail-value">{{ fileDetails.error_code }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Extracted Details -->
        <div v-if="fileDetails && fileDetails.transactions" class="extracted-details-section">
          <h2 class="section-header">
            <i class="las la-file-invoice"></i>
            Extracted Details
          </h2>
          <ExtractedDetails 
            :file-type="fileDetails.type" 
            :extracted-data="fileDetails.transactions" 
          />
        </div>

        <!-- Actions -->
        <div class="file-actions">
          <button @click="generateReport" class="action-btn primary" :disabled="generatingReport">
            <BaseSpinner v-if="generatingReport" size="small" />
            <i v-else class="las la-chart-bar"></i>
            Generate Report
          </button>
          <button @click="downloadFile" class="action-btn secondary" :disabled="!fileDetails.file_url">
            <i class="las la-download"></i>
            Download File
          </button>
          <button @click="deleteFile" class="action-btn danger" :disabled="deletingFile">
            <BaseSpinner v-if="deletingFile" size="small" />
            <i v-else class="las la-trash"></i>
            Delete File
          </button>
        </div>
      </div>
    </div>
    </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service'
import BaseSpinner from '@/components/UI/BaseSpinner.vue'
import ExtractedDetails from './components/ExtractedDetails.vue'

export default {
  name: 'FileDetails',
  components: {
    BaseSpinner,
    ExtractedDetails
  },
  props: {
    candidateId: {
      type: String,
      required: true
    },
    fileId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      fileDetails: null,
      loading: false,
      error: null,
      generatingReport: false,
      deletingFile: false
    }
  },
  mounted() {
    this.loadFileDetails()
  },
  methods: {
    async loadFileDetails() {
      this.loading = true
      this.error = null
      
      try {
        const details = await ChatbotService.getFileDetails(this.fileId)
        console.log('details', details)
        this.fileDetails = details
      } catch (err) {
        console.error('Error loading file details:', err)
        this.error = 'Failed to load file details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async generateReport() {
      this.generatingReport = true
      try {
        // Implement report generation logic
        console.log('Generating report for file:', this.fileId)
        // Add your report generation logic here
      } catch (err) {
        console.error('Error generating report:', err)
      } finally {
        this.generatingReport = false
      }
    },

    async deleteFile() {
      if (!confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
        return
      }

      this.deletingFile = true
      try {
        // Implement file deletion logic
        console.log('Deleting file:', this.fileId)
        // Add your file deletion logic here
        
        // Navigate back to files list after successful deletion
        this.$router.push(`/manage/chatbot/candidate/${this.candidateId}/files`)
      } catch (err) {
        console.error('Error deleting file:', err)
      } finally {
        this.deletingFile = false
      }
    },

    downloadFile() {
      if (this.fileDetails && this.fileDetails.file_url) {
        window.open(this.fileDetails.file_url, '_blank')
      }
    },

    getDocumentTypeLabel(type) {
      const typeMap = {
        'emirates_id': 'Emirates ID',
        'trade_license': 'Trade License',
        'vat_statement': 'VAT Statement',
        'aecb_report': 'AECB Report',
        'bank_statement': 'Bank Statement'
      }
      return typeMap[type] || type
    },

    getStatusLabel(status) {
      const statusMap = {
        'pending': 'Pending',
        'scanning': 'Scanning',
        'extracting': 'Extracting',
        'completed': 'Completed',
        'failed': 'Failed',
        'processing': 'Processing'
      }
      return statusMap[status] || status
    },

    getStatusClass(status) {
      const classMap = {
        'pending': 'status-pending',
        'scanning': 'status-processing',
        'extracting': 'status-processing',
        'completed': 'status-completed',
        'failed': 'status-failed',
        'processing': 'status-processing'
      }
      return classMap[status] || 'status-unknown'
    },

    formatFileSize(bytes) {
      if (!bytes) return 'Unknown'
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      return new Date(dateString).toLocaleDateString()
    },

    formatDateTime(dateString) {
      if (!dateString) return 'Unknown'
      return new Date(dateString).toLocaleString()
    },

    formatDuration(seconds) {
      if (!seconds) return 'Unknown'
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}m ${remainingSeconds}s`
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.file-details-page {
  background: #ffffff;
  min-height: 100vh;
}

.page-header {
  background: #ffffff;
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
  
  .breadcrumb-nav {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    
    .breadcrumb-link {
      color: #6c757d;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: color 0.2s ease;
      
      &:hover {
        color: $color-primary;
      }
    }
    
    .breadcrumb-separator {
      color: #adb5bd;
      margin: 0 0.5rem;
    }
    
    .breadcrumb-current {
      color: #495057;
      font-weight: 500;
    }
  }
  
  .page-title {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0 0 0.5rem 0;
    font-family: $brand-font-medium;
  }
  
  .page-subtitle {
    color: #718096;
    margin: 0;
    font-size: 1.1rem;
    font-family: $brand-font-regular;
  }
}

.main-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid $color-primary;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  p {
    color: #6c757d;
    font-size: 1.1rem;
    margin: 0;
  }
}

.error-state {
  .error-icon {
    font-size: 3rem;
    color: #e53e3e;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
  }
  
  .error-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }
}

.file-details-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.file-overview-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .file-header {
    display: flex;
    align-items: center;
    gap: 1rem;

    i {
      font-size: 3rem;
      color: $color-primary;
    }
    
    .file-icon {
      font-size: 3rem;
      color: $color-primary;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      height: 80px;
      background: rgba($color-primary, 0.1);
      border-radius: 12px;
    }
    
    .file-info {
      flex: 1;
      
      .file-name {
        font-size: 1.5rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0 0 0.5rem 0;
        font-family: $brand-font-medium;
      }
      
      .file-meta {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        
        span {
          color: #6c757d;
          font-size: 0.9rem;
          font-family: $brand-font-regular;
        }
      }
    }
    
    .file-status {
      .status-badge {
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }
  }
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.details-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .card-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
    
    i {
      color: $color-primary;
    }
    
    &.error-title {
      color: #e53e3e;
      
      i {
        color: #e53e3e;
      }
    }
  }
  
  .details-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f8f9fa;
    
    &:last-child {
      border-bottom: none;
    }
    
    .detail-label {
      font-weight: 500;
      color: #6c757d;
      font-family: $brand-font-medium;
    }
    
    .detail-value {
      color: #2d3748;
      font-family: $brand-font-regular;
    }
  }
}

.analysis-content {
  .analysis-summary, .key-findings {
    margin-bottom: 1.5rem;
    
    h4 {
      color: #2d3748;
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 0.5rem 0;
      font-family: $brand-font-medium;
    }
    
    p, li {
      color: #6c757d;
      line-height: 1.6;
      font-family: $brand-font-regular;
    }
    
    ul {
      margin: 0;
      padding-left: 1.5rem;
    }
  }
}

.error-content {
  .error-message {
    color: #e53e3e;
    font-weight: 500;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
  }
  
  .error-code {
    display: flex;
    gap: 1rem;
    align-items: center;
  }
}

.file-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  padding: 2rem 0;
  
  .action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    
    &.primary {
      background: $color-primary;
      color: white;
      
      &:hover:not(:disabled) {
        background: darken($color-primary, 10%);
        transform: translateY(-1px);
      }
    }
    
    &.secondary {
      background: #f8f9fa;
      color: #6c757d;
      border: 1px solid #e2e8f0;
      
      &:hover:not(:disabled) {
        background: #e9ecef;
        transform: translateY(-1px);
      }
    }
    
    &.danger {
      background: #e53e3e;
      color: white;
      
      &:hover:not(:disabled) {
        background: darken(#e53e3e, 10%);
        transform: translateY(-1px);
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.status-pending {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.status-processing {
    background: #dbeafe;
    color: #1e40af;
  }
  
  &.status-completed {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.status-failed {
    background: #fee2e2;
    color: #991b1b;
  }
  
  &.status-unknown {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.extracted-details-section {
  margin-top: 2rem;
  
  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1.5rem 0;
    font-family: $brand-font-medium;
    
    i {
      color: $color-primary;
      font-size: 1.3rem;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
  
  .file-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .file-header {
    flex-direction: column;
    text-align: center;
    
    .file-icon {
      align-self: center;
    }
  }
}
</style>
